import {
  createConnectQueryKey,
  disableQuery,
  useMutation,
  useQuery,
} from '@connectrpc/connect-query'
import { GroupClassAttendance } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/group_class_attendance_pb'
import { GroupClass } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/group_class_pb'
import { StoreConfig } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_config_pb'
import {
  cancelGroupClassAttendancePublic,
  getGroupClassAttendancePublic,
} from '@fingertip/creator-proto/gen/fingertip/creator/group_class_attendance/v1/group_class_attendance-GroupClassAttendanceService_connectquery'
import { getSite } from '@fingertip/creator-proto/gen/fingertip/creator/site/v1/site-SiteService_connectquery'
import { getStorePaymentComputationPublic } from '@fingertip/creator-proto/gen/fingertip/creator/store_payment/v1/store_payment-StorePaymentService_connectquery'
import { useQueryClient } from '@tanstack/react-query'
import { sentenceCase } from 'change-case'
import { format } from 'date-fns'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AlertBanner } from '@/components/shared/alert-banner'
import { Button } from '@/components/ui/button'
import { DialogTitle } from '@/components/ui/dialog'
import { ResponsiveSheetContent } from '@/components/ui/responsive-sheet-content'
import { Spinner } from '@/components/ui/spinner'
import { formatPriceInCents } from '@/lib/dinero/utils'
import { useToken } from '@/lib/hooks/use-token'

type Props = {
  groupClass: GroupClass
  groupClassAttendance: GroupClassAttendance
  storeConfig?: StoreConfig
  siteSlug: string
  cancellationFeeApplies: boolean
  cancellationFeeEnabled: boolean
  onClose: () => void
}

export const BookerSuccessCancelAttendanceDialog = ({
  groupClassAttendance,
  groupClass,
  storeConfig,
  siteSlug,
  cancellationFeeApplies,
  cancellationFeeEnabled,
  onClose,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const { callOptions, token } = useToken()
  const queryClient = useQueryClient()
  const {
    data: cancellationComputationData,
    error: cancellationComputationError,
    isLoading: cancellationComputationLoading,
  } = useQuery(
    getStorePaymentComputationPublic,
    !token || !groupClass.cancellationFeeInCents
      ? disableQuery
      : {
          siteSlug,
          priceInCents: groupClass.cancellationFeeInCents,
        },
    {
      callOptions,
    },
  )

  const cancelMutation = useMutation(cancelGroupClassAttendancePublic, {
    callOptions,
    onSuccess: () => {
      toast.success(t('successfully_cancelled'))
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey(getGroupClassAttendancePublic, {
          groupClassAttendanceId: groupClassAttendance?.id,
        }),
      })
      setLoading(false)
      onClose()
    },
    onError: (error) => {
      setLoading(false)
      toast.error(error.message)
    },
  })

  const handleCancel = useCallback(async () => {
    setLoading(true)
    cancelMutation.mutate({
      attendanceId: groupClassAttendance.id,
      siteSlug,
    })
  }, [cancelMutation, groupClassAttendance.id, siteSlug])

  const { data: siteData } = useQuery(
    getSite,
    !token
      ? disableQuery
      : {
          siteSlug,
        },
    {
      callOptions,
    },
  )

  const siteName = useMemo(() => {
    return siteData?.site?.name || siteSlug
  }, [siteData?.site?.name, siteSlug])

  if (cancellationComputationError) {
    return <AlertBanner>{cancellationComputationError.message}</AlertBanner>
  }

  if (cancellationComputationLoading) {
    return <Spinner />
  }

  return (
    <ResponsiveSheetContent
      desktopClassName="max-h-full overflow-auto"
      mobileClassName="w-full max-h-[calc(100vh-100px)] min-w-full overflow-auto"
    >
      <DialogTitle className="h3 font-semibold">
        {cancellationFeeApplies
          ? t('are_you_sure_you_want_to_cancel_with_fee')
          : t('are_you_sure_you_want_to_cancel')}
      </DialogTitle>
      <div className="flex flex-col gap-y-2">
        {cancellationFeeEnabled && cancellationFeeApplies && (
          <>
            <p>
              {t(
                'when_booking_your_appointment_you_have_agreed_to_the_cancellation_policy_of',
              )}{' '}
              <span className="font-semibold">{siteName}</span>
            </p>
            <p>
              {t('are_you_sure_you_want_to_cancel_your_booking')}{' '}
              {t('you_will_be_charged_a')}{' '}
              <span className="font-semibold">
                {formatPriceInCents(
                  groupClass.cancellationFeeInCents,
                  groupClass.currency,
                ) +
                  ' ' +
                  t('cancellation_fee')}
              </span>
            </p>

            {!!storeConfig?.hostDetails && (
              <>
                <p>
                  {t(
                    'alternatively_you_can_contact_the_host_to_request_cancellation',
                  )}
                  :
                </p>
                <div className="rounded-lg bg-muted p-6 text-muted-foreground">
                  {storeConfig?.hostDetails}
                </div>
              </>
            )}
            <div className="font-semibold">{t('payment_details')}</div>
            <div className="flex flex-col">
              <div className="flex flex-row justify-between">
                <span>{sentenceCase(t('cancellation_fee'))}</span>
                <span>
                  {formatPriceInCents(
                    groupClass.cancellationFeeInCents,
                    groupClass.currency,
                  )}
                </span>
              </div>
              {!!cancellationComputationData?.taxTotalInCents && (
                <div className="flex flex-row justify-between">
                  <span>{sentenceCase(t('tax_included'))}</span>
                  <span>
                    {formatPriceInCents(
                      cancellationComputationData.taxTotalInCents,
                      groupClass.currency,
                    )}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {cancellationFeeEnabled &&
          !cancellationFeeApplies &&
          groupClass.cancellationLateAfterDate && (
            <>
              <p>
                {t(
                  'when_booking_your_appointment_you_have_agreed_to_the_cancellation_policy_of',
                )}{' '}
                <span className="font-semibold">{siteName}</span>
              </p>
              <p>
                {t('because_you_are_cancelling_before')}:{' '}
                <span className="font-semibold">
                  {format(
                    groupClass.cancellationLateAfterDate.toDate(),
                    `EEE, MMM do, yyyy 'at' h:mm aaa`,
                  )}
                </span>
                {', '}
                {t('you_will_not_be_charged_a_cancellation_fee')}
              </p>
            </>
          )}
      </div>

      <div className="flex flex-row justify-end gap-2">
        <Button loading={loading} onClick={handleCancel}>
          {cancellationFeeApplies
            ? t('pay_and_cancel_this_booking')
            : t('cancel_this_booking')}
        </Button>
        <Button onClick={onClose} variant="secondary">
          {t('keep_booking')}
        </Button>
      </div>
    </ResponsiveSheetContent>
  )
}
